import axios from "axios";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

export const UPLOAD_ARTICLES_LIST_QUERY_KEY = "upload-articles-list";
export const UPLOAD_ARTICLES_PAGE_SIZE = 20;

export function getUploadArticleKey(article) {
  if (article?.id != null && String(article.id) !== "") {
    return String(article.id);
  }
  if (article?.asin) {
    return String(article.asin);
  }
  return null;
}

export function isPublishableUploadArticle(article) {
  return !article.status && article.amazon_ml_match !== false;
}

export function getUploadArticlesAuthHeaders() {
  return {
    Authorization: `Bearer ${
      JSON.parse(localStorage?.session)?.sessionInfo?.token
    }`,
  };
}

/**
 * @param {{
 *   page?: number;
 *   pageSize?: number;
 *   statusFilter?: string | null;
 *   categoryName?: string | null;
 *   search?: string;
 * }} params
 */
export async function fetchUploadArticlesList(
  {
    page = 1,
    pageSize = UPLOAD_ARTICLES_PAGE_SIZE,
    statusFilter = null,
    categoryName = null,
    search = "",
  } = {},
  { signal } = {}
) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  if (statusFilter) {
    params.set("statusFilter", statusFilter);
  }
  if (categoryName) {
    params.set("categoryName", categoryName);
  }
  if (search.trim()) {
    params.set("search", search.trim());
  }

  const response = await axios.get(
    `${process.env.REACT_APP_BACKEND_URL}/cargar-articulos/?${params.toString()}`,
    {
      headers: getUploadArticlesAuthHeaders(),
      signal,
    }
  );

  const data = response.data;

  if (Array.isArray(data)) {
    throw new TypeError(
      "El backend devolvió un array completo. Se requiere la respuesta paginada documentada en docs/BACKEND_PAGINADO_CARGAR_ARTICULOS.md"
    );
  }

  return {
    items: Array.isArray(data.items) ? data.items : [],
    pagination: {
      page: data.pagination?.page ?? page,
      pageSize: data.pagination?.pageSize ?? pageSize,
      total: data.pagination?.total ?? 0,
      totalPages: data.pagination?.totalPages ?? 1,
    },
    counts: {
      total: data.counts?.total ?? 0,
      processed: data.counts?.processed ?? 0,
      unprocessed: data.counts?.unprocessed ?? 0,
      price0: data.counts?.price0 ?? 0,
      prohibited: data.counts?.prohibited ?? 0,
      forbiddenCategory: data.counts?.forbiddenCategory ?? 0,
      forbiddenTitleChar: data.counts?.forbiddenTitleChar ?? 0,
      titleMismatch: data.counts?.titleMismatch ?? 0,
      totalPendingRetries: data.counts?.totalPendingRetries ?? 0,
    },
  };
}

export function useUploadArticlesList(filters) {
  return useQuery({
    queryKey: [UPLOAD_ARTICLES_LIST_QUERY_KEY, filters],
    queryFn: () => fetchUploadArticlesList(filters),
    placeholderData: keepPreviousData,
  });
}

/**
 * @param {{
 *   statusFilter?: string | null;
 *   categoryName?: string | null;
 *   search?: string;
 * }} filters
 * @param {{ signal?: AbortSignal }} options
 */
export async function fetchSelectableArticles(filters, { signal } = {}) {
  const params = new URLSearchParams();
  if (filters.statusFilter) {
    params.set("statusFilter", filters.statusFilter);
  }
  if (filters.categoryName) {
    params.set("categoryName", filters.categoryName);
  }
  if (filters.search?.trim()) {
    params.set("search", filters.search.trim());
  }

  const query = params.toString();
  const url = `${process.env.REACT_APP_BACKEND_URL}/cargar-articulos/selectable-items${
    query ? `?${query}` : ""
  }`;

  const response = await axios.get(url, {
    headers: getUploadArticlesAuthHeaders(),
    signal,
  });

  const data = response.data;

  let items = [];
  if (Array.isArray(data?.items)) {
    items = data.items;
  } else if (Array.isArray(data)) {
    items = data;
  }

  return {
    items: items.filter(
      (article) => !article.status && article.amazon_ml_match !== false
    ),
    total: data?.total ?? items.length,
  };
}

/**
 * Obtiene todos los artículos publicables del filtro activo.
 * Usa GET /cargar-articulos/selectable-items (una sola llamada al backend).
 *
 * @param {{
 *   statusFilter?: string | null;
 *   categoryName?: string | null;
 *   search?: string;
 * }} filters
 * @param {{
 *   signal?: AbortSignal;
 *   onProgress?: (progress: { loaded: number; total: number }) => void;
 * }} options
 */
export async function fetchAllSelectableArticles(
  filters,
  { signal, onProgress } = {}
) {
  onProgress?.({ loaded: 0, total: 0 });

  const { items, total } = await fetchSelectableArticles(filters, { signal });

  onProgress?.({ loaded: items.length, total });

  return items;
}
